import { useEffect, useRef, useState } from 'react'
import { Users, Plus, Filter, X } from 'lucide-react'
import { cn } from '@/utils/cn'
import {
  FilterBar,
  FilterSearch,
  FilterSelect,
  FilterPanelButton,
  FilterClearButton,
} from '@/components/ui/page-toolbar'
import { getStageLabel } from '@/components/Deals/deals-constants'
import type { DealStage } from '@/types'

const selectClass =
  'w-full rounded-lg border border-[var(--glass-border)] bg-[var(--glass-bg)] px-3 py-2 text-sm text-text-primary outline-none focus:border-accent/50'

interface RocketSearchFiltersProps {
  searchTerm: string
  onSearchChange: (value: string) => void
  closerFilter: string
  onCloserFilterChange: (value: string) => void
  etapaFilter: string
  onEtapaFilterChange: (value: string) => void
  stages: DealStage[]
  closers: { id: string; name: string }[]
  isAdmin: boolean
  onNewDeal: () => void
}

export function RocketSearchFilters({
  searchTerm,
  onSearchChange,
  closerFilter,
  onCloserFilterChange,
  etapaFilter,
  onEtapaFilterChange,
  stages,
  closers,
  isAdmin,
  onNewDeal,
}: RocketSearchFiltersProps) {
  const [filtersOpen, setFiltersOpen] = useState(false)
  const filtersRootRef = useRef<HTMLDivElement>(null)

  const advancedFilterCount =
    (etapaFilter !== 'all' ? 1 : 0)

  const hasActiveFilter =
    searchTerm.trim().length > 0 ||
    (isAdmin && closerFilter !== '') ||
    etapaFilter !== 'all'

  const clearFilters = () => {
    onSearchChange('')
    onCloserFilterChange('')
    onEtapaFilterChange('all')
    setFiltersOpen(false)
  }

  useEffect(() => {
    if (!filtersOpen) return
    const handlePointerDown = (event: MouseEvent) => {
      if (filtersRootRef.current && !filtersRootRef.current.contains(event.target as Node)) {
        setFiltersOpen(false)
      }
    }
    document.addEventListener('mousedown', handlePointerDown)
    return () => document.removeEventListener('mousedown', handlePointerDown)
  }, [filtersOpen])

  return (
    <FilterBar>
      <FilterSearch
        value={searchTerm}
        onChange={onSearchChange}
        placeholder='Buscar deal, empresa ou serviço...'
        ariaLabel='Buscar deals'
      />

      {isAdmin && closers.length > 0 && (
        <FilterSelect
          value={closerFilter}
          onChange={onCloserFilterChange}
          icon={<Users size={15} className='text-text-muted' aria-hidden />}
          title='Filtrar por closer'
          ariaLabel='Filtrar por closer'
        >
          <option value=''>Todos os Closers</option>
          {closers.map((closer) => (
            <option key={closer.id} value={closer.id}>
              {closer.name}
            </option>
          ))}
        </FilterSelect>
      )}

      <div ref={filtersRootRef} className='relative shrink-0'>
        <FilterPanelButton
          active={filtersOpen}
          badgeCount={advancedFilterCount}
          expanded={filtersOpen}
          onClick={() => setFiltersOpen((open) => !open)}
        />

        {filtersOpen && (
          <>
            <div
              className='fixed inset-0 z-40 bg-black/40 md:hidden'
              aria-hidden
              onClick={() => setFiltersOpen(false)}
            />
            <div
              role='dialog'
              aria-label='Filtros avançados'
              className={cn(
                'z-50 rounded-xl border border-[var(--glass-border)] bg-[var(--bg-card)] p-4 shadow-2xl',
                'fixed inset-x-4 bottom-4 max-h-[70vh] overflow-y-auto md:absolute md:inset-auto md:right-0 md:top-full md:mt-2 md:w-80',
              )}
            >
              <div className='mb-4 flex items-center justify-between'>
                <h3 className='text-sm font-semibold text-text-primary'>Filtros avançados</h3>
                <button
                  type='button'
                  onClick={() => setFiltersOpen(false)}
                  className='flex h-8 w-8 items-center justify-center rounded-lg text-text-muted hover:bg-white/[0.06] hover:text-text-primary'
                  aria-label='Fechar filtros'
                >
                  <X size={16} />
                </button>
              </div>

              <div className='flex flex-col gap-4'>
                {stages.length > 1 && (
                  <label className='flex flex-col gap-1.5'>
                    <span className='flex items-center gap-1.5 text-xs font-medium text-text-muted'>
                      <Filter size={13} aria-hidden />
                      Etapa
                    </span>
                    <select
                      className={selectClass}
                      value={etapaFilter}
                      onChange={(e) => onEtapaFilterChange(e.target.value)}
                    >
                      <option value='all'>Todas as etapas</option>
                      {stages.map((stage) => (
                        <option key={stage} value={stage}>
                          {getStageLabel(stage)}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
              </div>

              <div className='mt-4 flex gap-2 border-t border-[var(--glass-border)] pt-4'>
                <button
                  type='button'
                  onClick={clearFilters}
                  className='flex-1 rounded-lg border border-[var(--glass-border)] bg-[var(--glass-bg)] px-3 py-2 text-sm font-medium text-text-muted transition-all hover:bg-white/[0.06] hover:text-text-primary'
                >
                  Limpar tudo
                </button>
                <button
                  type='button'
                  onClick={() => setFiltersOpen(false)}
                  className='flex-1 rounded-lg bg-accent px-3 py-2 text-sm font-medium text-white transition-all hover:opacity-90'
                >
                  Aplicar
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {hasActiveFilter && <FilterClearButton onClick={clearFilters} />}

      <button
        type='button'
        onClick={onNewDeal}
        className='ml-auto flex h-10 shrink-0 items-center gap-1.5 rounded-lg bg-accent px-3 text-xs font-semibold text-white transition-all hover:opacity-90'
      >
        <Plus size={14} aria-hidden />
        Nova Negociação
      </button>
    </FilterBar>
  )
}
